import { formatMetric, type MealPlanClinicalLimit } from './analysis.shared';
import type { RowTone } from './NutritionReportSectionTable';

export function formatStructuredClinicalLimit(limit?: MealPlanClinicalLimit): string | undefined {
  if (!limit) return undefined;
  const format = (min?: number, max?: number, unit = limit.unit): string | undefined => {
    if (limit.kind === 'minimum' && min != null) return `≥${formatMetric(min)}${unit}`;
    if (limit.kind === 'maximum' && max != null) return `≤${formatMetric(max)}${unit}`;
    if (limit.kind === 'range' && min != null && max != null) return `${formatMetric(min)}–${formatMetric(max)}${unit}`;
    return undefined;
  };
  const grams = format(limit.min, limit.max);
  const percent = format(limit.minPercentEnergy, limit.maxPercentEnergy, '%E' as typeof limit.unit);
  return percent && grams ? `${percent} / ${grams}` : percent ?? grams;
}

export function clinicalLimitStatus(args: {
  limit: MealPlanClinicalLimit;
  intake: number;
  actualEnergy?: number;
  kcalPerGram?: number;
}): { progress: number; tone: RowTone; explanation?: string } | null {
  const { limit, intake, kcalPerGram } = args;
  // These are daily targets based on final clinical calories, never energy eaten so far.
  const basis = limit.basisCalories;
  const convert = (percent?: number) => percent != null && basis && kcalPerGram
    ? percent * basis / 100 / kcalPerGram : undefined;
  const min = limit.min ?? convert(limit.minPercentEnergy);
  const max = limit.max ?? convert(limit.maxPercentEnergy);
  let tone: RowTone;
  let target: number;
  if (limit.kind === 'minimum' && min != null && min > 0) {
    tone = intake < min ? 'deficient' : max != null && intake > max ? 'excess' : 'met';
    target = min;
  } else if (limit.kind === 'maximum' && max != null && max > 0) {
    tone = intake > max ? 'excess' : 'met';
    target = max;
  } else if (limit.kind === 'range' && min != null && max != null && max > 0) {
    tone = intake < min ? 'deficient' : intake > max ? 'excess' : 'met';
    target = max;
  } else return null;
  return { progress: intake / target * 100, tone, explanation: limit.explanation };
}
